import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BoardHeaderActions from '../components/BoardHeaderActions';
import { useBoardFeature } from '../sharedBoard';
import { Alert, Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import CalculateRounded from '@mui/icons-material/CalculateRounded';
import dayjs from 'dayjs';
import { useWideBoard } from '../../DashboardRoute';
import {
  downloadHeatmapImage, downloadPlannerExport, fetchCampaignBreakdown, fetchFilterOptions, fetchPlannerDashboard,
  fetchPlannerHeatmap,
  fetchPlannerSimulation,
} from '../../../services/manualDashboardService';
import type {
  CampaignBreakdownPayload, HeatmapPayload, PlannerDashboardPayload, SimulatorPayload,
} from '../../../types/plannerDashboard';
import type { FilterDef } from '../../../types/manualDashboard';
import {
  DATE_PRESETS,
  formatDateRange,
  InfoHint,
  MetricCard,
  NumberField,
  PageHeader,
  Panel,
  SidePanelDock,
  ToolbarButton,
  ValueWithDelta,
  resolvePreset,
  type DateRange,
  type FreshnessStatus,
} from '../../../ui-kit';
import { createSurfaceChatStore } from '../../surface-chat/createSurfaceChatStore';
import { useSurfaceChat } from '../../surface-chat/useSurfaceChat';
import BoardChatPanel from '../components/BoardChatPanel';
import DashboardToolbar from '../components/DashboardToolbar';
import { useBoardZone } from '../useBoardZone';
import CampaignsPanel from './components/CampaignsPanel';
import CampaignHeatmap from './components/CampaignHeatmap';
import CampaignFunnelDialog from './components/CampaignFunnelDialog';
import SimulatorPanel, { type SimulatorGoal } from './components/SimulatorPanel';
import SpendMovesPanel from './components/SpendMovesPanel';
import ConversionChart from './components/ConversionChart';
import DaysTable from './components/DaysTable';
import {
  buildCampaignsSurfaceContext, buildPlannerSurfaceContext, buildSimulatorSurfaceContext,
  campaignsChat, plannerChat, simulatorChat,
} from './plannerChat';
import { agentsLabel, dollars, listDays, oneDecimal, percent, whole } from './plannerFormat';

const DEFAULT_PRESET = DATE_PRESETS.last90Days;

/**
 * The calendar runs to today, so any range can be picked, and the board shows
 * the dates picked: no waiting period, the newest weeks read as they stand.
 * Only today is left out (`range.used_end` is yesterday at most), because the
 * warehouse holds data to yesterday, and the board says so under the headline.
 */
const today = () => dayjs();
const PRESETS = [
  DATE_PRESETS.last30Days,
  DATE_PRESETS.last90Days,
  DATE_PRESETS.thisQuarter,
  DATE_PRESETS.lastQuarter,
  DATE_PRESETS.yearToDate,
];

type Plan = Record<string, number>;

// One conversation per page, not per board: the staffing plan, Campaign
// Performance and the simulator are three pages to the reader, and a question
// asked on one should not be waiting in the chat of another.
const chatStores = new Map<string, ReturnType<typeof createSurfaceChatStore>>();
function chatStoreFor(key: string, name: string) {
  let store = chatStores.get(key);
  if (!store) {
    store = createSurfaceChatStore(name);
    chatStores.set(key, store);
  }
  return store;
}

/**
 * A planner board: enter a plan, see how each day of the week carries it.
 *
 * The plan (agents, leads per agent, target) redraws the board a moment after
 * the last change, so stepping a number up three times costs one request.
 * Channels start on the spec's defaults — Facebook and Google — and the date
 * range on the last 90 days.
 */
type CalcRates = { contact: number; conversion: number };
const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * The period's contact rate and contact to submit from the board's own counts
 * (every lead in the channels and dates chosen), for when the simulator cannot
 * answer — its figures need spend, the rates do not. Each weekday's figures are
 * daily averages over that many weeks, so they are weighted back to totals.
 */
function ratesFromDays(days: PlannerDashboardPayload['days']): CalcRates | null {
  const leads = days.reduce((n, d) => n + d.leads_available * d.weeks, 0);
  const contacted = days.reduce((n, d) => n + d.contacted_available * d.weeks, 0);
  const submits = days.reduce((n, d) => n + d.submits_available * d.weeks, 0);
  if (!leads || !contacted) return null;
  return { contact: round1((100 * contacted) / leads), conversion: round1((100 * submits) / contacted) };
}

/** The period's own rates, as the boxes show them: to one decimal of a percent. */
const actualRates = (today: SimulatorPayload['today']): CalcRates => ({
  contact: round1(100 * today.contact),
  conversion: round1(100 * today.conversion),
});

/**
 * Same spend, same leads, other rates: spend ÷ (leads × contact × contact to
 * submit). At the period's own rates it returns the period's own figures, not
 * a recalculation from the rounded rates in the boxes — otherwise the
 * untouched calculator would read $2,050 beside a "Today" of $2,056.
 */
function costAtRates(today: SimulatorPayload['today'], rates: CalcRates) {
  const actual = actualRates(today);
  if (rates.contact === actual.contact && rates.conversion === actual.conversion) {
    return { submits: today.submits_day, costPerSubmit: today.cost_per_submit };
  }
  const submits = today.leads_day * (rates.contact / 100) * (rates.conversion / 100);
  return { submits, costPerSubmit: submits > 0 ? today.spend_day / submits : null };
}

/**
 * One board, opened three ways, each its own page with its own dates, filters
 * and chat, so each can be its own row in a sidebar:
 *
 * - `board` (/dashboard/<id>) — the staffing plan: agents, leads per agent,
 *   the days and the campaigns each day uses.
 * - `simulator` (/dashboard/<id>/simulator) — "Cost / Submit Simulator": the two cost
 *   targets, Simulate, and the paths to them.
 * - `campaigns` (/dashboard/<id>/campaigns) — "Campaign Performance": every
 *   campaign by day of the week. It used to sit at the foot of the board;
 *   it answers a marketing question, not a staffing one.
 *
 * Each view fetches only what it shows.
 */
export type PlannerView = 'board' | 'simulator' | 'campaigns';

const VIEW_TITLES: Record<Exclude<PlannerView, 'board'>, { title: string; description: string }> = {
  simulator: {
    title: 'Cost / Submit Simulator',
    description: 'Here we can see what a submit would have cost us if the contact rate or contact to submit had been different, and every way we could reach a cost per submit we choose.',
  },
  campaigns: {
    title: 'Campaign Performance',
    description: 'Each campaign, by day of the week, hour of day or cohort: of its leads, how many reached a later step. Pick the steps to compare, or click a campaign for its hours.',
  },
};

export default function PlannerDashboardPage({ view = 'board' }: { view?: PlannerView } = {}) {
  const isBoard = view === 'board';
  const hasPlanInputs = view !== 'campaigns';
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const navigate = useNavigate();
  const feature = useBoardFeature();

  const [payload, setPayload] = React.useState<PlannerDashboardPayload | null>(null);
  const [range, setRange] = React.useState<DateRange>(() => resolvePreset(DEFAULT_PRESET, today(), 1));
  // Unset until the reader changes a filter: the board opens on the spec's
  // defaults, Facebook and Google. The simulator opens on every channel
  // instead, with the filter there to narrow it — which campaigns are worth
  // the money is its question, so it should not start by leaving any out.
  const [filters, setFilters] = React.useState<Record<string, string[]> | undefined>(
    () => (view === 'simulator' ? { channel: [] } : undefined),
  );
  const [plan, setPlan] = React.useState<Plan>({});
  const [sentPlan, setSentPlan] = React.useState<Plan>({});
  const [options, setOptions] = React.useState<FilterDef[]>([]);
  const [optionsLoading, setOptionsLoading] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const [downloadProblem, setDownloadProblem] = React.useState<{ block: string; message: string } | null>(null);
  const [dow, setDow] = React.useState(1);
  // The heatmap's own controls and its own request: changing the rate or the
  // minimum should not rebuild the plan above it.
  const [grid, setGrid] = React.useState<HeatmapPayload | null>(null);
  const [gridPick, setGridPick] = React.useState<{
    from?: string; to?: string; minLeads?: number; by?: string;
  }>({});
  const [gridLoading, setGridLoading] = React.useState(true);
  const [gridError, setGridError] = React.useState<string | null>(null);
  // The clock Campaign Performance's hours read in: the same menu, and the
  // same remembered pick per viewer, as the Daily Call Plan's (21 Oct 2026).
  const [zone, pickZone] = useBoardZone(dashboardId);
  // The campaign clicked on the grid, and its funnel hour by hour.
  const [openCampaign, setOpenCampaign] = React.useState<{ channel: string; campaign: string } | null>(null);
  const [breakdown, setBreakdown] = React.useState<CampaignBreakdownPayload | null>(null);
  const [breakdownLoading, setBreakdownLoading] = React.useState(false);
  const [breakdownError, setBreakdownError] = React.useState<string | null>(null);
  // The simulator's goal and its own request: a what-if on cost per submit
  // should not refetch the plan, and the plan's inputs do not move it.
  const [simGoal, setSimGoal] = React.useState<SimulatorGoal>({ costPerSubmit: 1200, maxCostPerLead: 60, maxAgents: null });
  const [sentSimGoal, setSentSimGoal] = React.useState<SimulatorGoal>(simGoal);
  const [sim, setSim] = React.useState<SimulatorPayload | null>(null);
  const [simLoading, setSimLoading] = React.useState(true);
  const [simError, setSimError] = React.useState<string | null>(null);
  // The calculator above the simulator: what a submit would have cost over
  // these dates at another contact rate or contact to submit, the spend and
  // the leads left as they were. Rates in percent, as typed. It opens on the
  // period's own rates, and takes a new pair only on Calculate.
  const [calc, setCalc] = React.useState<CalcRates | null>(null);
  const [sentCalc, setSentCalc] = React.useState<CalcRates | null>(null);
  // Where the boxes start: the simulator's Today figures when it answers, so
  // the calculator and the scenarios agree to the dollar; the board's own
  // counts when it cannot (no spend in the warehouse), so the rates are still
  // there to see and change even while no cost can be worked out.
  const seedRates: CalcRates | null = sim
    ? actualRates(sim.today)
    : simError && payload ? ratesFromDays(payload.days) : null;
  const seedKey = seedRates ? `${sim ? 'sim' : 'board'}:${seedRates.contact}:${seedRates.conversion}:${sim?.today.spend_day ?? ''}` : '';
  React.useEffect(() => {
    // New dates or channels bring new actual rates: start again from them.
    if (!seedRates) return;
    setCalc(seedRates);
    setSentCalc(seedRates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);
  const [chatOpen, setChatOpen] = React.useState(false);

  useWideBoard(true, chatOpen);

  const filtersKey = filters === undefined ? '' : JSON.stringify(filters);
  const planKey = JSON.stringify(sentPlan);

  React.useEffect(() => {
    if (!dashboardId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPlannerDashboard(
      dashboardId,
      {
        values: sentPlan,
        // Once touched, a cleared filter is sent empty — every channel — not left
        // out, which would bring the defaults back.
        filters: filters === undefined ? undefined : { channel: filters.channel ?? [] },
        start: range.start,
        end: range.end,
        refresh: refreshNonce > 0,
      },
      controller.signal,
    )
      .then((data) => {
        setPayload(data);
        // Take the server's defaults into the fields once, on arrival. They go
        // into both the boxes and what was last run, so a board nobody has
        // touched does not open asking to be simulated.
        setPlan((current) => {
          if (Object.keys(current).length) return current;
          const defaults = Object.fromEntries(data.inputs.map((i) => [i.key, i.value]));
          setSentPlan(defaults);
          return defaults;
        });
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, planKey, filtersKey, range, refreshNonce]);

  // On the simulator's page the two cost targets wait for Generate scenarios:
  // a half-typed $1,20 is not a question anyone meant to ask, and the search
  // behind it takes a moment. On the board the staffing plan answers by
  // itself, a moment after the last change (below).
  const simKey = JSON.stringify(sentSimGoal);
  const dirty = JSON.stringify(simGoal) !== simKey;
  const simulate = () => {
    setSentSimGoal(simGoal);
  };

  // The board: agents, leads per agent and the target apply on their own,
  // once the reader has paused, so stepping a number up three times costs one
  // request rather than three.
  React.useEffect(() => {
    if (!isBoard || JSON.stringify(plan) === planKey) return undefined;
    const timer = window.setTimeout(() => setSentPlan(plan), 450);
    return () => window.clearTimeout(timer);
  }, [isBoard, plan, planKey]);

  React.useEffect(() => {
    if (!dashboardId || view !== 'simulator') return undefined;
    const controller = new AbortController();
    const goal = JSON.parse(simKey) as SimulatorGoal;
    setSimLoading(true);
    setSimError(null);
    fetchPlannerSimulation(
      dashboardId,
      {
        costPerSubmit: goal.costPerSubmit,
        maxCostPerLead: goal.maxCostPerLead,
        maxAgents: goal.maxAgents ?? undefined,
        filters: filters === undefined ? undefined : { channel: filters.channel ?? [] },
        start: range.start,
        end: range.end,
      },
      controller.signal,
    )
      .then(setSim)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setSimError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setSimLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, filtersKey, range, simKey, refreshNonce, view]);

  const gridKey = JSON.stringify(gridPick);

  React.useEffect(() => {
    if (!dashboardId || view !== 'campaigns') return undefined;
    const controller = new AbortController();
    const pick = JSON.parse(gridKey) as { from?: string; to?: string; minLeads?: number; by?: string };
    setGridLoading(true);
    setGridError(null);
    fetchPlannerHeatmap(
      dashboardId,
      {
        values: {},
        filters: filters === undefined ? undefined : { channel: filters.channel ?? [] },
        start: range.start,
        end: range.end,
        refresh: false,
        zone,
        ...pick,
      },
      controller.signal,
    )
      .then(setGrid)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setGridError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setGridLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, filtersKey, range, gridKey, refreshNonce, view, zone]);

  // A clicked campaign's hours, on the grid's own starting step, minimum,
  // channels and dates, so its "All hours" line is its row on the grid.
  const breakdownKey = openCampaign && grid
    ? JSON.stringify({ ...openCampaign, from: grid.from, minLeads: grid.min_leads, zone: grid.hours?.label })
    : '';
  React.useEffect(() => {
    if (!dashboardId || !breakdownKey) return undefined;
    const controller = new AbortController();
    const pick = JSON.parse(breakdownKey) as {
      channel: string; campaign: string; from: string; minLeads: number; zone?: string;
    };
    setBreakdownLoading(true);
    setBreakdownError(null);
    // Another campaign, or new dates, steps or zone: the hours held are for
    // something else, so the window shimmers rather than show them as current.
    setBreakdown(null);
    fetchCampaignBreakdown(
      dashboardId,
      {
        values: {},
        filters: filters === undefined ? undefined : { channel: filters.channel ?? [] },
        start: range.start,
        end: range.end,
        refresh: false,
        ...pick,
      },
      controller.signal,
    )
      .then(setBreakdown)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setBreakdownError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setBreakdownLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, filtersKey, range, breakdownKey, refreshNonce]);

  React.useEffect(() => {
    if (!dashboardId) return undefined;
    const controller = new AbortController();
    setOptionsLoading(true);
    fetchFilterOptions(dashboardId, { start: range.start, end: range.end }, controller.signal)
      .then((data) => setOptions(data.filters))
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setOptionsLoading(false);
      });
    return () => controller.abort();
  }, [dashboardId, range, refreshNonce]);

  // The chat introduces itself as the page, never as the board it is cut from.
  const pageTitle = isBoard ? (payload?.spec.name ?? 'Dashboard') : VIEW_TITLES[view].title;
  const chatKey = `${dashboardId ?? 'unknown'}:${view}`;
  const chatStore = chatStoreFor(chatKey, pageTitle);
  const surfaceContextRef = React.useRef<Record<string, unknown> | null>(null);
  const getSurfaceContext = React.useCallback(() => surfaceContextRef.current, []);
  // Found from the database rather than remembered in this browser, and keyed
  // by the view: each page of this board keeps its own conversation.
  const { sendMessage, sendSQLApproval, stopStream } = useSurfaceChat({
    useStore: chatStore,
    dashboardId: dashboardId ?? '',
    view,
    getSurfaceContext,
    enabled: chatOpen,
  });

  if (feature.loading) {
    return <Box sx={{ px: 0.5 }}><Skeleton variant="rounded" height={320} /></Box>;
  }
  if (!feature.enabled) {
    return (
      <Box sx={{ px: 0.5 }}>
        <Alert severity="info">
          Manual dashboards are not enabled for this workspace. Ask an admin to turn on the
          &ldquo;Manual Dashboards&rdquo; feature.
        </Alert>
      </Box>
    );
  }
  if (error && !payload) {
    return (
      <Box sx={{ px: 0.5 }}>
        <Alert severity="error" onClose={() => navigate('/dashboard')}>{error}</Alert>
      </Box>
    );
  }
  if (!payload) {
    return (
      <Stack spacing={1.5} sx={{ px: 0.5 }}>
        <Skeleton variant="rounded" height={72} />
        <Skeleton variant="rounded" height={96} />
        <Skeleton variant="rounded" height={104} />
        <Skeleton variant="rounded" height={320} />
      </Stack>
    );
  }

  {
    const channelsOnScreen = payload.filters.find((f) => f.key === 'channel')?.selected ?? [];
    const base = {
      dashboardId: dashboardId ?? '',
      title: pageTitle,
      channels: channelsOnScreen,
      range: { start: payload.range.used_start, end: payload.range.used_end },
    };
    surfaceContextRef.current =
      view === 'campaigns' ? buildCampaignsSurfaceContext(base, grid, openCampaign)
        : view === 'simulator'
          ? buildSimulatorSurfaceContext(base, sim, sim && sentCalc
            ? { ...sentCalc, cost_per_submit: costAtRates(sim.today, sentCalc).costPerSubmit }
            : null)
          : { ...buildPlannerSurfaceContext(dashboardId ?? '', payload), view: 'board' };
  }

  const input = (key: string) => payload.inputs.find((i) => i.key === key)!;
  const value = (key: string) => plan[key] ?? input(key).value;
  const agents = value('agents');
  const perAgent = value('leads_per_agent');
  const target = value('target');
  // The figures answer the plan that was sent; the fields may be a step ahead.
  const shownTarget = input('target').value;
  const shownPerAgent = input('leads_per_agent').value;
  const { summary, days } = payload;
  const channelFilter = payload.filters.find((f) => f.key === 'channel');
  // What the filter panel shows as picked: the reader's choice, or the defaults.
  const applied = filters ?? Object.fromEntries(payload.filters.map((f) => [f.key, f.selected]));

  const freshness: FreshnessStatus = payload.meta.refreshing
    ? 'updating'
    : payload.meta.status === 'failed' ? 'failed' : payload.meta.stale ? 'stale' : 'fresh';

  const exportQuery = {
    values: Object.fromEntries(payload.inputs.map((i) => [i.key, i.value])),
    filters: channelFilter ? { channel: channelFilter.selected } : undefined,
    start: range.start,
    end: range.end,
  };
  const download = async (block: string, order?: { key: string; direction: string } | null) => {
    if (!dashboardId) return;
    setDownloading(block);
    setDownloadProblem(null);
    try {
      // The heatmap's download is of what is on screen, so it carries the
      // pair of steps and the minimum the reader picked.
      const heat = block === 'heatmap' && grid
        ? { from: grid.from, to: grid.to, minLeads: grid.min_leads,
            by: grid.by, zone: grid.hours?.label,
            sort: order?.key ?? null, dir: order?.direction }
        : block === 'heatmap_campaign' && breakdown
          ? { from: breakdown.from, minLeads: breakdown.min_leads, by: breakdown.by,
              channel: breakdown.channel, campaign: breakdown.campaign, zone: breakdown.hours?.label }
          : {};
      await downloadPlannerExport(dashboardId, { ...exportQuery, block, ...heat });
    } catch (err) {
      setDownloadProblem({ block, message: `Could not download: ${(err as Error).message}` });
    } finally {
      setDownloading(null);
    }
  };

  /** Where the money moves on one scenario, for the targets it was run with. */
  const downloadMoves = async (pathKey: string) => {
    if (!dashboardId || !sim) return;
    setDownloading('moves');
    setDownloadProblem(null);
    try {
      await downloadPlannerExport(dashboardId, {
        ...exportQuery,
        block: 'moves',
        simCostPerSubmit: sim.goal.cost_per_submit,
        simMaxCostPerLead: sim.goal.max_cost_per_lead,
        simPath: pathKey,
      });
    } catch (err) {
      setDownloadProblem({ block: 'moves', message: `Could not download: ${(err as Error).message}` });
    } finally {
      setDownloading(null);
    }
  };

  /** The heatmap as a picture, for the two image formats in its menu. */
  const downloadGrid = async (fmt: 'png' | 'svg', order?: { key: string; direction: string } | null) => {
    if (!dashboardId || !grid) return;
    setDownloading('heatmap');
    setDownloadProblem(null);
    try {
      await downloadHeatmapImage(dashboardId, {
        fmt,
        from: grid.from,
        to: grid.to,
        minLeads: grid.min_leads,
        by: grid.by,
        zone: grid.hours?.label,
        sort: order?.key ?? null,
        dir: order?.direction,
        filters: filters === undefined ? undefined : { channel: filters.channel ?? [] },
        start: range.start,
        end: range.end,
      });
    } catch (err) {
      setDownloadProblem({ block: 'heatmap', message: `Could not download: ${(err as Error).message}` });
    } finally {
      setDownloading(null);
    }
  };

  const planText = `With ${agentsLabel(input('agents').value)} at ${whole(shownPerAgent)} leads each`;
  const sentence = (() => {
    if (payload.range.empty) return 'There is no data for this date range yet: the data runs to yesterday. Pick a range that includes an earlier day.';
    const parts: string[] = [];
    if (summary.days_hitting_target === 7) {
      parts.push(`${planText}, every day hits ${whole(shownTarget)}%.`);
    } else {
      parts.push(`${planText}, ${summary.days_hitting_target} of 7 days hit ${whole(shownTarget)}%.`);
      parts.push(`${listDays(summary.days_below_target)} ${summary.days_below_target.length === 1 ? 'falls' : 'fall'} short.`);
    }
    if (summary.days_short.length) {
      parts.push(`There aren't enough leads on ${listDays(summary.days_short)}.`);
    }
    parts.push(
      summary.most_agents_every_day > 0
        ? `To hit ${whole(shownTarget)}% every day, plan for at most ${agentsLabel(summary.most_agents_every_day)}.`
        : `No number of agents hits ${whole(shownTarget)}% every day with these campaigns.`,
    );
    return parts.join(' ');
  })();

  const limitingDay = days.length ? days.reduce((a, b) => (b.most_agents < a.most_agents ? b : a)) : null;

  const board = (
    <Box sx={{ px: 0.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: 0 }}>
      <PageHeader
        ariaLabel="About this dashboard"
        title={isBoard ? payload.spec.name : VIEW_TITLES[view].title}
        description={isBoard ? payload.spec.description ?? undefined : VIEW_TITLES[view].description}
        actions={
          <BoardHeaderActions
            dashboardId={dashboardId ?? ''}
            name={isBoard ? payload.spec.name : VIEW_TITLES[view].title}
            view={isBoard ? null : view}
            updatedAt={payload.meta.built_at}
            status={freshness}
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); setRefreshNonce((n) => n + 1); }}
            loading={loading}
            chatOpen={chatOpen}
            onToggleChat={() => setChatOpen((v) => !v)}
            chatTooltip="Ask about this dashboard"
          />
        }
      />

      {/* The data the plan is judged against — dates and channels — in the
          cohort board's own toolbar. The plan itself gets the card below. */}
      <DashboardToolbar
        dateRange={range}
        onDateRangeChange={setRange}
        datePresets={PRESETS}
        minDate={payload.range.history_start}
        maxDate={dayjs().format('YYYY-MM-DD')}
        dateDisplay="day"
        dateLabel="Lead dates"
        filters={options}
        applied={applied}
        onApply={setFilters}
        loading={loading}
        optionsLoading={optionsLoading}
        chatOpen={chatOpen}
        // The clock the hours read in, on Campaign Performance: the Daily
        // Call Plan's menu.
        zone={view === 'campaigns' && grid?.hours ? { ...grid.hours.zone_picker, onChange: pickZone } : null}
      />

      {payload.meta.status === 'failed' && payload.meta.error && (
        <Alert severity="warning">
          Could not refresh from the warehouse, so these are the numbers from{' '}
          {payload.meta.built_at ? new Date(payload.meta.built_at).toLocaleString() : 'the last successful build'}.
          <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
            {payload.meta.error}
          </Typography>
        </Alert>
      )}
      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      {view === 'simulator' && (() => {
        const canCost = !!sim;
        const actual = sim ? actualRates(sim.today) : seedRates;
        const result = sim && sentCalc ? costAtRates(sim.today, sentCalc) : null;
        const calcDirty = JSON.stringify(calc) !== JSON.stringify(sentCalc);
        const atActual = !!actual && !!sentCalc
          && sentCalc.contact === actual.contact && sentCalc.conversion === actual.conversion;
        const sentence = !sim || !result || !sentCalc
          ? null
          : atActual
            ? `Over these dates a submit cost us ${dollars(sim.today.cost_per_submit)}. We spent ${dollars(sim.today.spend_day)} a day `
              + `on ${oneDecimal(sim.today.leads_day)} leads a day, our agents spoke with ${percent(sentCalc.contact)} of them, and `
              + `${percent(sentCalc.conversion)} of those sent an application. Change the contact rate or contact to submit to see `
              + 'what a submit would have cost us instead.'
            : `If our agents had spoken with ${percent(sentCalc.contact)} of the leads, and ${percent(sentCalc.conversion)} of those `
              + `had sent an application, the same ${dollars(sim.today.spend_day)} a day spent on the same `
              + `${oneDecimal(sim.today.leads_day)} leads would have given us ${oneDecimal(result.submits)} submits a day instead of `
              + `${oneDecimal(sim.today.submits_day)}. A submit would then have cost us ${dollars(result.costPerSubmit)} instead of `
              + `${dollars(sim.today.cost_per_submit)}.`;
        return (
          <Panel
            title="Cost per submit calculator"
            subtitle="Change the contact rate or contact to submit and press Calculate to see what a submit would have cost us over the dates above. The spend and the number of leads stay exactly as they were."
            info="Cost per submit is the spend divided by the number of submits. The number of submits is the leads we bought, times the contact rate, times contact to submit. We use the paid campaigns in the dates and channels above, so these are the same figures as the Today row in the scenarios below."
            loading={!calc && !simError}
            loadingHeight={120}
          >
            {calc && sentCalc ? (
              <Stack spacing={1.75}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1.5} aria-label="Calculator">
                  <NumberField
                    label="Contact rate"
                    suffix="%"
                    value={calc.contact}
                    min={0.1}
                    max={100}
                    step={0.1}
                    onChange={(v) => setCalc((c) => (c ? { ...c, contact: v } : c))}
                    size="medium"
                  />
                  <NumberField
                    label="Contact to submit"
                    suffix="%"
                    value={calc.conversion}
                    min={0.1}
                    max={100}
                    step={0.1}
                    onChange={(v) => setCalc((c) => (c ? { ...c, conversion: v } : c))}
                    size="medium"
                  />
                  <ToolbarButton
                    icon={<CalculateRounded />}
                    size="medium"
                    variant={calcDirty && canCost ? 'brand' : 'outlined'}
                    disabled={!calcDirty || !canCost}
                    onClick={() => setSentCalc(calc)}
                    tooltip={!canCost
                      ? 'We need the spend data to work this out, and the warehouse is missing it right now'
                      : calcDirty
                        ? 'Work out what a submit would cost with these numbers'
                        : 'Change the contact rate or contact to submit to calculate again'}
                  >
                    Calculate
                  </ToolbarButton>
                  {calcDirty && canCost && (
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.warning })}>
                      Press Calculate to use these
                    </Typography>
                  )}
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  {/* No figure, no big dash: a hero-sized "—" reads as a broken
                      number. Say what it is waiting for, quietly, instead. */}
                  {!result ? (
                    <Stack spacing={0.25}>
                      <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
                        Cost per submit
                      </Typography>
                      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                        Waiting for spend data
                      </Typography>
                    </Stack>
                  ) : (
                  <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="h3" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {dollars(result.costPerSubmit)}
                    </Typography>
                    <Typography variant="body2" component="span" sx={(t) => ({ color: t.tokens.text.secondary })}>
                      a submit
                    </Typography>
                    {!atActual && result.costPerSubmit !== null && sim && sim.today.cost_per_submit !== null && (
                      <ValueWithDelta value={result.costPerSubmit} against={sim.today.cost_per_submit} lowerIsBetter>
                        <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                          vs {dollars(sim.today.cost_per_submit)}
                        </Box>
                      </ValueWithDelta>
                    )}
                  </Stack>
                  )}
                </Stack>
                <Divider />
                {canCost ? (
                  <Typography variant="body1" aria-live="polite">{sentence}</Typography>
                ) : (
                  // The rates are real; only the cost is held back. Said as a
                  // passing state, not as the reader's mistake.
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.warning })}>
                    {simError ?? 'We cannot work out the cost per submit right now.'} The contact rate and contact to
                    submit shown here are the real figures for these dates. We will show the cost per submit as soon as
                    the spend data is back.
                  </Typography>
                )}
              </Stack>
            ) : simError ? (
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.warning })}>{simError}</Typography>
            ) : null}
          </Panel>
        );
      })()}

      {/* The plan is what this board is for, so it is a card of its own:
          the numbers someone enters, and straight under them what they mean. */}
      {hasPlanInputs && (
      <Panel
        title={isBoard ? 'Your plan' : 'Cost per submit scenarios'}
        subtitle={isBoard
          ? 'Change the number of agents, the leads each agent gets, or the target, and everything below updates for your plan.'
          : 'Set the cost per submit we want to reach and the most we are willing to pay for one lead, then press Generate scenarios. We will show every way we could get there.'}
        loading={false}
      >
        <Stack spacing={1.75}>
          <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1.5} aria-label="Plan">
            {isBoard && (<>
            <NumberField
              label={input('agents').label}
              value={agents}
              min={input('agents').min}
              max={input('agents').max}
              onChange={(v) => setPlan((p) => ({ ...p, agents: v }))}
              size="medium"
            />
            <Typography variant="h5" component="span" sx={(t) => ({ color: t.tokens.text.tertiary })} aria-hidden>×</Typography>
            <NumberField
              label={input('leads_per_agent').label}
              value={perAgent}
              min={input('leads_per_agent').min}
              max={input('leads_per_agent').max}
              onChange={(v) => setPlan((p) => ({ ...p, leads_per_agent: v }))}
              size="medium"
            />
            <Typography variant="h5" component="span" sx={(t) => ({ color: t.tokens.text.tertiary })} aria-hidden>=</Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ whiteSpace: 'nowrap' }}>
              <Typography variant="h3" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {whole(agents * perAgent)}
              </Typography>
              <Typography variant="body2" component="span" sx={(t) => ({ color: t.tokens.text.secondary })}>
                leads needed a day
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <NumberField
              label={input('target').label}
              value={target}
              suffix={input('target').suffix ?? undefined}
              min={input('target').min}
              max={input('target').max}
              onChange={(v) => setPlan((p) => ({ ...p, target: v }))}
              size="medium"
            />
            <InfoHint label="About the target" title={input('target').definition ?? ''} />
            </>)}
            {/* The cost targets and their button belong to the simulator's own
                page; the staffing plan answers as its numbers change. */}
            {!isBoard && (<>
            <NumberField
              label="Cost per submit target"
              prefix="$"
              value={simGoal.costPerSubmit}
              min={100}
              max={10000}
              step={50}
              onChange={(v) => setSimGoal((g) => ({ ...g, costPerSubmit: v }))}
              size="medium"
            />
            <NumberField
              label="A lead may cost up to"
              prefix="$"
              value={simGoal.maxCostPerLead}
              min={5}
              max={500}
              step={5}
              onChange={(v) => setSimGoal((g) => ({ ...g, maxCostPerLead: v }))}
              size="medium"
            />
            {/* Off while the answer below already matches the boxes — pressing
                it then would change nothing — and it lights up in the brand
                tint the moment a number changes. A new date range or filter
                re-runs on its own, so nothing is stranded behind the button. */}
            <ToolbarButton
              icon={<PlayArrowRounded />}
              size="medium"
              variant={dirty ? 'brand' : 'outlined'}
              disabled={!dirty}
              onClick={simulate}
              loading={simLoading}
              loadingLabel="Generating"
              tooltip={dirty ? 'Show every way we could reach this cost per submit' : 'Change a number above to run it again'}
            >
              Generate scenarios
            </ToolbarButton>
            {dirty && (
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.warning })}>
                Press Generate scenarios to use these
              </Typography>
            )}
            </>)}
          </Stack>

          {isBoard && (<>
          <Divider />

          <Box aria-live="polite">
            <Typography variant="body1">{sentence}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>
                Based on leads from {formatDateRange({ start: payload.range.used_start, end: payload.range.used_end })}
                {payload.range.trimmed_newest ? ' · today is not in the data yet' : ''}
                {channelFilter?.selected.length ? ` · ${channelFilter.selected.join(', ')}` : ' · all channels'}
              </Typography>
              <InfoHint
                label="About the dates used"
                title={`The data runs to yesterday, so today's leads are added tomorrow. Recent weeks show their leads as they stand: some of those leads are still being worked and applying.`}
              />
            </Stack>
          </Box>
          </>)}
        </Stack>
      </Panel>
      )}

      {isBoard && (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        <MetricCard
          label="Leads coming in a day"
          value={whole(summary.leads_available)}
          info={
            <>
              On an average day, how many new leads (MQLs) come in from the channels you picked, over the dates you
              picked.
              <br /><br />
              Compare it with the leads your plan needs ({whole(summary.leads_needed)} a day). The line underneath
              names the quietest and busiest days of the week.
            </>
          }
          note={summary.quietest_day ? `Quietest ${summary.quietest_day} · busiest ${summary.busiest_day}` : ' '}
          loading={loading}
        />
        <MetricCard
          label={`Days hitting ${whole(shownTarget)}%`}
          value={`${summary.days_hitting_target} of 7`}
          info={
            <>
              On how many days of the week your plan reaches your {whole(shownTarget)}% target.
              <br /><br />
              For each day, agents get leads from that day&apos;s best campaigns first, then the next best, until
              every agent has {whole(shownPerAgent)}. If the leads we contact from those campaigns still apply at{' '}
              {whole(shownTarget)}% or more, the day hits the target. The line underneath names the days that
              don&apos;t.
            </>
          }
          note={summary.days_below_target.length ? `Under on ${summary.days_below_target.join(', ')}` : 'Every day'}
          loading={loading}
        />
        <MetricCard
          label="Days short of leads"
          value={`${summary.days_short.length} of 7`}
          info={
            <>
              On how many days of the week there aren&apos;t enough leads for your plan, even using every campaign.
              <br /><br />
              Your plan needs {whole(summary.leads_needed)} leads a day. On these days fewer than that come in, so
              some agents would not get their {whole(shownPerAgent)}. The line underneath names the day with the
              biggest gap.
            </>
          }
          note={summary.biggest_gap ? `Most on ${summary.biggest_gap.day}: ${whole(summary.biggest_gap.leads)} leads` : 'Enough leads every day'}
          loading={loading}
        />
        <MetricCard
          label={`Max agents to hit ${whole(shownTarget)}% every day`}
          value={agentsLabel(summary.most_agents_every_day)}
          info={
            <>
              The most agents you can schedule on every day of the week while each still gets{' '}
              {whole(shownPerAgent)} leads and every day still hits {whole(shownTarget)}%.
              <br /><br />
              Each extra agent needs more leads, and those come from weaker campaigns, so conversion drops. One agent
              more than this, and at least one day runs out of leads or falls under the target. The tightest day is
              named underneath; the Max agents column below shows each day&apos;s own limit.
            </>
          }
          note={
            limitingDay
              ? `At ${whole(shownPerAgent)} leads each · ${limitingDay.name} is the tightest day`
              : ' '
          }
          loading={loading}
        />
      </Box>
      )}

      {view === 'simulator' && <SimulatorPanel payload={sim} loading={simLoading} error={simError} />}

      {/* For the scenarios that move money: which campaigns get more, which
          less, and how much. Only on the simulator's own page. */}
      {view === 'simulator' && !simError && (
        <SpendMovesPanel
          payload={sim}
          loading={simLoading}
          onDownload={downloadMoves}
          downloading={downloading === 'moves'}
          problem={downloadProblem?.block === 'moves' ? downloadProblem.message : null}
        />
      )}

      {isBoard && (<>
      <DaysTable
        days={days}
        target={shownTarget}
        leadsPerAgent={shownPerAgent}
        loading={loading}
        onDownload={() => download('days')}
        downloading={downloading === 'days'}
        problem={downloadProblem?.block === 'days' ? downloadProblem.message : null}
      />

      <ConversionChart days={days} target={shownTarget} loading={loading} />

      <CampaignsPanel
        days={days}
        dow={dow}
        onDowChange={setDow}
        target={shownTarget}
        loading={loading}
        onDownload={() => download('campaigns')}
        downloading={downloading === 'campaigns'}
        problem={downloadProblem?.block === 'campaigns' ? downloadProblem.message : null}
      />
      </>)}

      {view === 'campaigns' && (
      <CampaignHeatmap
        payload={grid}
        loading={gridLoading}
        error={gridError}
        onChange={(next) => setGridPick((current) => ({ ...current, ...next }))}
        onDownload={(what, order) => (what === 'xlsx' ? download('heatmap', order) : downloadGrid(what, order))}
        downloading={downloading === 'heatmap'}
        problem={downloadProblem?.block === 'heatmap' ? downloadProblem.message : null}
        onOpenCampaign={setOpenCampaign}
      />
      )}
      {view === 'campaigns' && (
      <CampaignFunnelDialog
        target={openCampaign}
        payload={breakdown}
        loading={breakdownLoading}
        error={breakdownError}
        onClose={() => setOpenCampaign(null)}
        onDownload={() => download('heatmap_campaign')}
        downloading={downloading === 'heatmap_campaign'}
        problem={downloadProblem?.block === 'heatmap_campaign' ? downloadProblem.message : null}
      />
      )}
    </Box>
  );

  return (
    <SidePanelDock
      open={chatOpen}
      storageKey="klaris_board_chat_width"
      label="Resize chat panel"
      panel={
        <BoardChatPanel
          useStore={chatStore}
          boardName={pageTitle}
          subtitle={isBoard ? undefined : 'Answers from this page'}
          scopeLabel={isBoard ? undefined : 'This page'}
          place={view === 'campaigns' ? campaignsChat : view === 'simulator' ? simulatorChat : plannerChat}
          onClose={() => setChatOpen(false)}
          onSend={sendMessage}
          onStop={stopStream}
          onSQLApproval={sendSQLApproval}
          contextChips={[
            // What this page's answer is scoped to — each page its own inputs.
            ...(view === 'board'
              ? [{ key: 'plan', label: `${whole(input('agents').value)} agents × ${whole(shownPerAgent)} · target ${whole(shownTarget)}%` }]
              : view === 'simulator' && sim
                ? [{ key: 'goal', label: `${dollars(sim.goal.cost_per_submit)} a submit · lead up to ${dollars(sim.goal.max_cost_per_lead)}` }]
                : view === 'campaigns' && grid
                  ? [{
                      key: 'steps',
                      label: `${grid.stages.find((st) => st.key === grid.from)?.label ?? grid.from} → ${grid.stages.find((st) => st.key === grid.to)?.label ?? grid.to} · min ${whole(grid.min_leads)} a day`,
                    }]
                  : []),
            {
              key: 'range',
              label: formatDateRange(
                view === 'campaigns' && grid ? grid.range : { start: payload.range.used_start, end: payload.range.used_end },
              ),
            },
            ...payload.filters
              .filter((f) => f.selected.length > 0)
              .map((f) => ({
                key: f.key,
                label: `${f.label}: ${f.selected[0]}${f.selected.length > 1 ? ` +${f.selected.length - 1}` : ''}`,
              })),
          ]}
        />
      }
    >
      {board}
    </SidePanelDock>
  );
}
